import React, { PropsWithChildren } from "react";
import { Divider, Grid } from "semantic-ui-react";
import AppForm from "../../components/form/Form";
import { FULLNAME_REGEXP } from "../../app/helpers/regexp";
import { useAppFormContext } from "../../components/form/useAppFormContext";
import useIndustries from "../../app/hooks/useIndustries";
import useHardcodedCities from "../../app/hooks/useHardcodedCities";

interface Props {
  isEditMode?: boolean;
}

const InternshipForm = ({ isEditMode }: PropsWithChildren<Props>) => {
  const { watch } = useAppFormContext();
  const { industryOptions } = useIndustries();
  const { cityOptions } = useHardcodedCities();
  const useHqAddress = watch("useHqAddress");
  const isRemote = watch("remote");
  const isCreateMode = !isEditMode;

  const titleField = (
    <AppForm.Group name="title" label="internship.form.title">
      <AppForm.Input name="title" rules={{ required: true }} maxLength={100} />
    </AppForm.Group>
  );

  const descriptionField = (
    <AppForm.Group name="description" label="internship.form.description">
      <AppForm.TextArea name="description" rules={{ required: true }} maxLength={1000} />
    </AppForm.Group>
  );

  const industryField = (
    <AppForm.Group name="industryIds" label="internship.form.industry">
      <AppForm.Select
        search
        name="industryIds"
        options={industryOptions}
        rules={{ required: true }}
        multiple
        defaultValue={[]}
      />
    </AppForm.Group>
  );

  const maxParticipantField = (
    <AppForm.Group name="participantsCapacity" label="internship.form.max_participants_capacity">
      <AppForm.Integer
        name="participantsCapacity"
        maxLength={5}
        rules={{
          required: true,
          min: {
            value: 1,
            message: "internship.form.errors.min_participants_capacity",
          },
          max: {
            value: 4,
            message: "internship.form.errors.max_participants_capacity",
          },
        }}
      />
    </AppForm.Group>
  );

  const startDateField = (
    <AppForm.Group name="startDate" label="internship.form.start_date">
      <AppForm.Date
        name="startDate"
        rules={{
          required: true,
          validate: {
            futureDate: (value: string) => (value < new Date().toISOString() ? "errors.futureDate" : true),
          },
        }}
      />
    </AppForm.Group>
  );

  const endDateField = (
    <AppForm.Group name="endDate" label="internship.form.end_date">
      <AppForm.Date
        name="endDate"
        rules={{
          required: true,
          validate: {
            futureDate: (value: string) => (value < new Date().toISOString() ? "errors.futureDate" : true),
            minDuration: (value: string) => {
              const startDate = watch("startDate");
              if (!startDate || !value) return true;
              const start = new Date(startDate);
              const end = new Date(value);
              let businessDays = 0;
              const current = new Date(start);
              while (current <= end) {
                const day = current.getDay();
                if (day !== 0 && day !== 6) businessDays++;
                current.setDate(current.getDate() + 1);
              }
              return businessDays >= 3 ? true : "internship.form.errors.min_duration";
            },
            maxDuration: (value: string) => {
              const startDate = watch("startDate");
              if (!startDate || !value) return true;
              const start = new Date(startDate);
              const maxEnd = new Date(start);
              maxEnd.setMonth(maxEnd.getMonth() + 2);
              return new Date(value) <= maxEnd ? true : "internship.form.errors.max_duration";
            },
          },
        }}
      />
    </AppForm.Group>
  );

  const remoteField = (
    <AppForm.Group name="remote">
      <AppForm.Checkbox name="remote" label="internship.form.remote" />
    </AppForm.Group>
  );

  const fullNameField = (
    <AppForm.Group name="responsiblePerson.fullName" label="internship.form.responsible_person.fullname">
      <AppForm.Input
        name="responsiblePerson.fullName"
        maxLength={50}
        rules={{
          required: true,
          pattern: {
            value: FULLNAME_REGEXP,
            message: "internship.form.errors.fullname",
          },
        }}
      />
    </AppForm.Group>
  );

  const jobTitleField = (
    <AppForm.Group name="responsiblePerson.jobTitle" label="internship.form.responsible_person.job_title">
      <AppForm.Input name="responsiblePerson.jobTitle" rules={{ required: true }} maxLength={50} />
    </AppForm.Group>
  );

  const responsibleEmailField = (
    <AppForm.Group name="responsiblePerson.email" label="internship.form.responsible_person.email">
      <AppForm.Email name="responsiblePerson.email" rules={{ required: true }} maxLength={50} />
    </AppForm.Group>
  );

  const responsiblePhoneField = (
    <AppForm.Group name="responsiblePerson.phoneNumber" label="internship.form.responsible_person.phone">
      <AppForm.Input name="responsiblePerson.phoneNumber" type="tel" maxLength={16} />
    </AppForm.Group>
  );

  const useHqField = (
    <AppForm.Group name="useHqAddress">
      <AppForm.Checkbox name="useHqAddress" label="internship.form.use_hq_address" />
    </AppForm.Group>
  );

  const cityField = (
    <AppForm.Group name="city" label="internship.form.city">
      <AppForm.Select name="city" options={cityOptions} rules={{ required: true }} disableAutoSelect />
    </AppForm.Group>
  );

  const addressField = (
    <AppForm.Group name="address" label="internship.form.address">
      <AppForm.Input name="address" rules={{ required: true }} maxLength={50} />
    </AppForm.Group>
  );

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column>
          {titleField}
          {descriptionField}
          {industryField}
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={8}>{startDateField}</Grid.Column>
        <Grid.Column width={8}>{endDateField}</Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={16}>{maxParticipantField}</Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={16}>{remoteField}</Grid.Column>
      </Grid.Row>

      {isCreateMode && (
        <>
          <Divider />

          <Grid.Row>
            <Grid.Column width={8}>{fullNameField}</Grid.Column>
            <Grid.Column width={8}>{jobTitleField}</Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>{responsibleEmailField}</Grid.Column>
            <Grid.Column width={8}>{responsiblePhoneField}</Grid.Column>
          </Grid.Row>
        </>
      )}

      {!isRemote && (
        <>
          <Divider />

          <Grid.Row>
            <Grid.Column width={16}>{useHqField}</Grid.Column>
          </Grid.Row>

          {!useHqAddress && (
            <Grid.Row>
              <Grid.Column width={8}>{cityField}</Grid.Column>
              <Grid.Column width={8}>{addressField}</Grid.Column>
            </Grid.Row>
          )}
        </>
      )}
    </Grid>
  );
};

export default InternshipForm;
